import { _ } from "../../../polyglot/polyglot.js";
import { difference } from "../../../helpers/set-util.js";
import { plusIcon, trashAltIcon } from "../../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import KeyFigureSelectionWidget from "../KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import popoverActionButtonWidget from "../../popoverActionButtonWidget.js";
import sortableListWidget from "../../sortableListWidget/sortableListWidget.js";

/**
 * Renders a list of key figures. The order of key figures can be
 * changed by drag&drop. Each key figure can be removed from the list
 * thanks to a remove button next to each. Below the list, a button
 * allows to remove all key figures from the list and another one
 * allows for adding key figures to the list.
 *
 * @param {function} onListUpdated - This function is called each
 * time the list of key figures is changed. An array of key figures is
 * passed as argument.
 * @param {function} onListSorted - This function is called each time
 * the order of key figures is changed. An array of key figures is
 * passed as argument.
 */
export default class SortableKeyFigureListEditor extends Widget {
	constructor({
		onListUpdated = (_items) => {},
		onListSorted = (_items) => {},
	} = {}) {
		super(...arguments);

		this._onListUpdated = onListUpdated;
		this._onListSorted = onListSorted;

		this._myKeyFigures = new Set([]);
		this._allKeyFigures = new Set([]);

		this._sortableList = sortableListWidget({
			itemRenderer: (keyFigure) => keyFigure.getName(),
			itemRemoved: (keyFigure) => this._removeKeyFigure(keyFigure),
			itemSorted: () => this._onListSorted(this._sortableList.getItems()),
		});
	}

	setup({ myKeyFigures = [], allKeyFigures = [] }) {
		this._myKeyFigures = new Set(myKeyFigures);
		this._allKeyFigures = new Set(allKeyFigures);

		this._sortableList.setItems(myKeyFigures);

		this.update();
	}

	getSelectedItems() {
		return [...this._myKeyFigures];
	}

	renderContentOn(html) {
		html.div(
			this._renderSortableListOn.bind(this),
			this._renderActionsOn.bind(this),
		);
	}

	_renderSortableListOn(html) {
		html.render(this._sortableList);
	}

	_renderActionsOn(html) {
		html.div(
			{ class: "btn-group", role: "group" },
			this._renderAddKeyFiguresButtonOn.bind(this),
			this._renderClearKeyFiguresButtonOn.bind(this),
		);
	}

	_renderClearKeyFiguresButtonOn(html) {
		html.button(
			{
				"data-test": "clear",
				class: "btn btn-default icon-and-text",
				click: () => this._removeAllKeyFigures(),
			},
			trashAltIcon({ class: "fa-large" }),
			_("Clear"),
		);
	}

	_renderAddKeyFiguresButtonOn(html) {
		const keyFigurePicker = this._makeKeyFigurePicker();
		const buttonLabel = (html) =>
			html.div(
				{ class: "icon-and-text" },
				plusIcon({ class: "fa-large" }),
				_("Add"),
			);

		const popoverButton = popoverActionButtonWidget({
			dataTestName: "add",
			label: buttonLabel,
			isDisabled: !this._canAddKeyFigures(),
			popoverHeader: _("Show more KPIs"),
			popoverContent: keyFigurePicker,
			popoverAction: () =>
				this._addKeyFigures(keyFigurePicker.getSelectedItems()),
			buttonType: "btn-primary",
			popoverPrimaryButtonLabel: buttonLabel,
		});

		html.render(popoverButton);
	}

	_makeKeyFigurePicker() {
		const keyFigurePicker = new KeyFigureSelectionWidget({
			multipleSelection: true,
		});

		keyFigurePicker.setup({ keyFigures: this._keyFiguresThatCanBeAdded() });

		return keyFigurePicker;
	}

	_canAddKeyFigures() {
		return this._keyFiguresThatCanBeAdded().length > 0;
	}

	_keyFiguresThatCanBeAdded() {
		return [...difference(this._allKeyFigures, this._myKeyFigures)];
	}

	_addKeyFigures(keyFiguresToAdd) {
		keyFiguresToAdd.forEach((keyFigureToAdd) =>
			this._myKeyFigures.add(keyFigureToAdd),
		);

		this._onMyKeyFiguresChanged();
	}

	_removeKeyFigure(keyFigureToRemove) {
		this._myKeyFigures.delete(keyFigureToRemove);
		this._onMyKeyFiguresChanged();
	}

	_removeAllKeyFigures() {
		this._myKeyFigures.clear();
		this._onMyKeyFiguresChanged();
	}

	_onMyKeyFiguresChanged() {
		this._sortableList.setItems([...this._myKeyFigures]);
		this._onListUpdated([...this._myKeyFigures]);
	}
}
